import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion, useScroll, useTransform } from "framer-motion";
import { ClipboardList, MessagesSquare, Coins, WifiOff, ReceiptText, Users, ArrowRight, ArrowUpRight, Hexagon } from "lucide-react";
import { MaskedLines, FadeUp, FadeIn } from "@/components/Reveal";
import EditorialMarquee from "@/components/Marquee";
import StoreBadges from "@/components/StoreBadges";
import FoundingKeeper from "@/components/FoundingKeeper";
import ReferralRewards from "@/components/ReferralRewards";
import Countdown from "@/components/Countdown";
import { LightboxImage } from "@/components/Lightbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const uniquePoints = [
  { icon: ClipboardList, title: "Biology-first inspection logs", copy: "Designed around real hive biology, not generic farm checklists." },
  { icon: MessagesSquare, title: "Ask a Bee™", copy: "AI that understands beekeeping, not generic agriculture." },
  { icon: Coins, title: "BeeCredits™", copy: "A reward system that grows with your apiary." },
  { icon: WifiOff, title: "Offline-First (KeeperSync™)", copy: "Works in the field with no cell signal — syncs when you're back." },
  { icon: ReceiptText, title: "CPA-ready reports", copy: "Honey harvest and expense reports your accountant will love." },
  { icon: Users, title: "Built by beekeepers", copy: "For beekeepers — of every experience level, at every scale." },
];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], [0, 140]);

  return (
    <section ref={ref} className="relative bg-forest-deep text-bone overflow-hidden" data-testid="home-hero">
      <motion.div style={{ y: bgY }} className="absolute inset-0" aria-hidden="true">
        <img
          src="/assets/brand/asset_aug12.png"
          alt=""
          className="w-full h-[120%] object-cover object-[68%_center] md:object-center opacity-40 md:opacity-45"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-forest-deep/75 via-forest-deep/55 to-forest-deep/95" aria-hidden="true" />

      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-24 md:pt-36 pb-24 md:pb-36 relative">
        <div className="max-w-3xl">
          <FadeIn delay={0.1}>
            <p className="text-xs uppercase tracking-[0.3em] text-honey mb-8" data-testid="hero-eyebrow">
              A product of Herrell Interactive
            </p>
          </FadeIn>
          <h1 className="font-display font-medium tracking-tighter leading-[1.04] text-[clamp(2.3rem,9vw,6.5rem)]" data-testid="hero-heading">
            <MaskedLines
              lines={[
                <span key="a">Your hives deserve</span>,
                <span key="b">better than</span>,
                <span key="c">a <em className="text-honey not-italic font-semibold">notebook.</em></span>,
              ]}
              delay={0.25}
            />
          </h1>
          <FadeUp delay={0.9} className="mt-8 max-w-xl">
            <p className="text-base md:text-lg text-bone/80 leading-relaxed" data-testid="hero-subcopy">
              ApiaryKeeper puts every inspection, every harvest, and every hive health
              signal in one place — so you spend less time on records and more time
              with your bees.
            </p>
          </FadeUp>
          <FadeUp delay={1.05} className="mt-10 flex flex-wrap items-center gap-4">
            <Link
              to="/pre-register?src=home-hero"
              data-testid="hero-join-button"
              className="group inline-flex items-center gap-2 bg-honey text-forest font-semibold px-8 py-4 rounded-full text-sm md:text-base transition-colors duration-300 hover:bg-honey-light"
            >
              Join the Launch List
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/features"
              data-testid="hero-learn-more-button"
              className="inline-flex items-center gap-2 text-bone/90 border border-bone/30 px-8 py-4 rounded-full text-sm md:text-base transition-colors duration-300 hover:border-honey hover:text-honey"
            >
              Learn More
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </FadeUp>
          <FadeUp delay={1.2} className="mt-10">
            <Countdown />
          </FadeUp>
          <FadeUp delay={1.35} className="mt-10">
            <StoreBadges />
          </FadeUp>
        </div>
      </div>
    </section>
  );
};

const UniqueSection = () => (
  <section className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32" data-testid="unique-section">
    <FadeUp className="mb-14 max-w-2xl">
      <p className="text-xs uppercase tracking-[0.3em] text-honey-dark mb-6">Why ApiaryKeeper</p>
      <h2 className="font-display text-4xl md:text-6xl font-medium tracking-tighter text-forest leading-[1.02]">
        What makes ApiaryKeeper <em className="text-honey-dark">unique.</em>
      </h2>
    </FadeUp>
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
      {uniquePoints.map((p, i) => (
        <FadeUp key={p.title} delay={(i % 3) * 0.08}>
          <div
            data-testid={`unique-card-${i + 1}`}
            className="h-full bg-white border border-forest/10 rounded-3xl p-8 transition-colors duration-500 hover:border-honey"
          >
            <p.icon className="w-8 h-8 text-honey-dark mb-6" strokeWidth={1.5} />
            <h3 className="font-display text-2xl text-forest tracking-tight mb-2">{p.title}</h3>
            <p className="text-ink/60 text-sm leading-relaxed">{p.copy}</p>
          </div>
        </FadeUp>
      ))}
    </div>
  </section>
);

const ShowcaseSection = () => (
  <section className="max-w-6xl mx-auto px-6 md:px-10 pb-24 md:pb-32" data-testid="app-showcase-section">
    <FadeUp className="text-center mb-12">
      <p className="text-xs uppercase tracking-[0.3em] text-honey-dark mb-5">The app, up close</p>
      <h2 className="font-display text-4xl md:text-5xl font-medium tracking-tighter text-forest">
        Every screen, built for the bee yard.
      </h2>
      <p className="text-ink/50 text-sm mt-4">Tap to view full screen.</p>
    </FadeUp>
    <FadeUp delay={0.15}>
      <LightboxImage
        src="/assets/brand/asset_5.png"
        alt="ApiaryKeeper app screens — hive tracking, inspection logs, AI insights, BeeCredits, and Ask a Bee"
        testid="app-showcase-image"
        className="w-full h-auto rounded-[2rem] border border-forest/10"
      />
    </FadeUp>
  </section>
);

const CTABand = () => {
  const [count, setCount] = useState(null);
  useEffect(() => {
    axios.get(`${API}/signups/count`).then((r) => setCount(r.data.count)).catch(() => {});
  }, []);

  return (
    <section className="bg-forest text-bone" data-testid="cta-band">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32 text-center">
        <FadeUp>
          <Hexagon className="w-10 h-10 text-honey mx-auto mb-8 animate-drift" strokeWidth={1.5} />
          <h2 className="font-display text-4xl md:text-6xl font-medium tracking-tighter leading-[1.02] mb-6">
            The apiary, <em className="text-honey">organized.</em>
          </h2>
          <p className="text-bone/60 max-w-xl mx-auto mb-4 leading-relaxed">
            ApiaryKeeper launches soon. Join the launch list for early access,
            founding-member rates, and a say in what we build first.
          </p>
          {count !== null && count > 0 && (
            <p className="text-honey text-sm mb-8" data-testid="signup-count">
              {count} {count === 1 ? "beekeeper is" : "beekeepers are"} already on the list
            </p>
          )}
          <Link
            to="/pre-register?src=home-cta"
            data-testid="cta-join-button"
            className="group inline-flex items-center gap-2 bg-honey text-forest font-semibold px-10 py-4 rounded-full transition-colors duration-300 hover:bg-honey-light mt-4"
          >
            Join the Launch List
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </FadeUp>
      </div>
    </section>
  );
};

export default function Home() {
  return (
    <>
      <Hero />
      <EditorialMarquee />
      <UniqueSection />
      <ShowcaseSection />
      <FoundingKeeper />
      <ReferralRewards />
      <CTABand />
    </>
  );
}
